import { useState, useEffect, useCallback, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Shield, LogOut } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface SessionKickedDialogProps {
  open: boolean;
  deviceLabel: string | null;
  onStaySignedIn: () => void;
  onSignOut: () => void;
}

export function SessionKickedDialog({
  open,
  deviceLabel,
  onStaySignedIn,
  onSignOut,
}: SessionKickedDialogProps) {
  const [countdown, setCountdown] = useState(60);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!open) {
      setCountdown(60);
      return;
    }

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (mountedRef.current) onSignOut();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, onSignOut]);

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-destructive" />
            Session Conflict Detected
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <p>
              Your account was signed in on another device of the same type
              (desktop or mobile){deviceLabel ? ` — ${deviceLabel}` : ''}.
              Note: you can use one desktop and one mobile device at the same time.
            </p>
            <p>
              You will be automatically signed out in{' '}
              <span className="font-bold text-destructive">{countdown} seconds</span>.
            </p>
            <p className="text-xs text-muted-foreground">
              If this wasn't you, we recommend changing your password immediately.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={onSignOut}>
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
          <Button onClick={onStaySignedIn}>
            <Shield className="h-4 w-4 mr-2" />
            Stay Signed In
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
